<template>
  <header class="header">
    <div class="header-container">
      <!-- Left Section: Logo -->
      <div class="header-left">
        <router-link to="/app/dashboard" class="logo">
          <img src="@/assets/img/testpal-full.png" alt="TestPal" class="logo-full" />
          <img src="@/assets/img/testpal-logo-udai.png" alt="TestPal" class="logo-icon" />
        </router-link>
      </div>

      <!-- Center Section: Search -->
      <div class="header-center">
        <div class="search-box">
          <vue-feather type="search" size="18" class="search-icon" />
          <input 
            type="text" 
            placeholder="Search exams, questions, users..." 
            v-model="searchQuery"
            @keyup.enter="handleSearch"
          />
          <kbd class="search-shortcut">⌘K</kbd>
        </div>
      </div>

      <!-- Right Section: Actions & User -->
      <div class="header-right">
        <!-- Quick Actions -->
        <div class="header-actions">
          <button class="action-btn" @click="toggleTheme" title="Toggle Theme">
            <vue-feather :type="isDarkMode ? 'sun' : 'moon'" size="20" />
          </button>

          <!-- Notifications -->
          <div class="dropdown">
            <button class="action-btn notification-btn" data-bs-toggle="dropdown">
              <vue-feather type="bell" size="20" />
              <span v-if="notificationCount" class="notification-badge">{{ notificationCount }}</span>
            </button>
            <div class="dropdown-menu dropdown-menu-end notifications-dropdown">
              <div class="dropdown-header">
                <h6>Notifications</h6>
                <button v-if="notifications.length" class="mark-read-btn">Mark all read</button>
              </div>
              <div class="notifications-list">
                <a v-for="n in notifications" :key="n.id" href="#" class="notification-item">
                  <div class="notification-icon" :class="n.type">
                    <vue-feather :type="getNotificationIcon(n.type)" size="16" />
                  </div>
                  <div class="notification-content">
                    <p>{{ n.message }}</p>
                    <span class="notification-time">{{ n.time }}</span>
                  </div>
                </a>
                <div v-if="!notifications.length" class="empty-notifications">
                  <vue-feather type="bell-off" size="32" />
                  <p>No new notifications</p>
                </div>
              </div>
              <router-link to="/app/notifications" class="dropdown-footer">
                View all notifications
              </router-link>
            </div>
          </div>
        </div>

        <!-- User Profile -->
        <div class="dropdown user-dropdown">
          <button class="user-btn" data-bs-toggle="dropdown">
            <div class="user-avatar">
              <img :src="userAvatar" :alt="user?.fullName" />
              <span class="status-dot"></span>
            </div>
            <div class="user-info">
              <span class="user-name">{{ user?.fullName || 'Admin' }}</span>
              <span class="user-role">{{ formatRole(user?.role) }}</span>
            </div>
            <vue-feather type="chevron-down" size="16" class="chevron" />
          </button>
          <div class="dropdown-menu dropdown-menu-end user-menu-dropdown">
            <div class="user-menu-header">
              <div class="user-avatar large">
                <img :src="userAvatar" :alt="user?.fullName" />
              </div>
              <div class="user-details">
                <h6>{{ user?.fullName || 'Admin User' }}</h6>
                <p>{{ user?.email }}</p>
              </div>
            </div>
            <div class="user-menu-body">
              <router-link to="/app/settings/profile" class="user-menu-item">
                <vue-feather type="user" size="18" />
                <span>My Profile</span>
              </router-link>
              <router-link to="/app/settings/security" class="user-menu-item">
                <vue-feather type="shield" size="18" />
                <span>Security</span>
              </router-link>
              <router-link to="/app/settings/app" class="user-menu-item">
                <vue-feather type="settings" size="18" />
                <span>Preferences</span>
              </router-link>
            </div>
            <div class="user-menu-footer">
              <button @click="handleLogout" class="logout-btn">
                <vue-feather type="log-out" size="18" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile Menu Button -->
      <button class="mobile-menu-btn" @click="toggleMobileSidebar">
        <vue-feather type="menu" size="24" />
      </button>
    </div>
  </header>
</template>

<script>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useNotifications } from '@/composables/useNotifications'
import { useAlert } from '@/plugins/sweetalert'

export default {
  name: 'LayoutHeader',
  setup() {
    const router = useRouter()
    const { toast } = useNotifications()
    const authStore = useAuthStore()
    const alert = useAlert()
    
    const user = computed(() => authStore.user)
    const searchQuery = ref('')
    const isDarkMode = ref(false)
    const notifications = ref([
      { id: 1, type: 'success', message: 'New exam submission received', time: '2 min ago' },
      { id: 2, type: 'info', message: '5 questions pending review', time: '1 hour ago' }
    ])
    const notificationCount = computed(() => notifications.value.length)

    const userAvatar = computed(() => {
      if (user.value?.avatar) return user.value.avatar
      const name = user.value?.fullName || 'U'
      const initial = name.charAt(0).toUpperCase()
      return `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><linearGradient id="g" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" style="stop-color:%23003049"/><stop offset="100%" style="stop-color:%23073b4c"/></linearGradient></defs><circle cx="50" cy="50" r="50" fill="url(%23g)"/><text x="50" y="62" text-anchor="middle" fill="white" font-size="40" font-family="system-ui">${initial}</text></svg>`
    })

    const formatRole = (role) => {
      if (!role) return 'Administrator'
      return role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }

    const getNotificationIcon = (type) => {
      const icons = { success: 'check-circle', info: 'info', warning: 'alert-triangle', error: 'x-circle' }
      return icons[type] || 'bell'
    }

    const toggleMobileSidebar = () => {
      document.body.classList.toggle('slide-nav')
    }

    const toggleTheme = () => {
      isDarkMode.value = !isDarkMode.value
      document.body.classList.toggle('dark-mode')
      toast.info(isDarkMode.value ? 'Dark mode enabled' : 'Light mode enabled')
    }

    const handleSearch = () => {
      if (searchQuery.value.trim()) {
        console.log('Search:', searchQuery.value)
      }
    }

    const handleLogout = async () => {
      let timerInterval
      const result = await alert.raw.fire({
        title: 'Sign Out?',
        html: 'You will be signed out in <b>5</b> seconds.',
        icon: 'question',
        timer: 5000,
        timerProgressBar: true,
        showCancelButton: true,
        confirmButtonText: 'Sign Out Now',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
        customClass: {
          popup: 'swal-popup-custom',
          title: 'swal-title-custom',
          htmlContainer: 'swal-html-custom',
          confirmButton: 'swal-confirm-btn',
          cancelButton: 'swal-cancel-btn',
          actions: 'swal-actions-custom',
          icon: 'swal-icon-custom'
        },
        buttonsStyling: false,
        didOpen: () => {
          const timer = alert.raw.getPopup().querySelector('b')
          timerInterval = setInterval(() => {
            const timeLeft = Math.ceil(alert.raw.getTimerLeft() / 1000)
            timer.textContent = timeLeft
          }, 100)
        },
        willClose: () => {
          clearInterval(timerInterval)
        }
      })

      if (result.isConfirmed || result.dismiss === alert.raw.DismissReason.timer) {
        await authStore.logout()
        toast.success('You have been signed out successfully')
        router.push({ name: 'login' })
      }
    }

    return {
      user, searchQuery, isDarkMode, notifications, notificationCount,
      userAvatar, formatRole, getNotificationIcon, toggleMobileSidebar,
      toggleTheme, handleSearch, handleLogout
    }
  }
}
</script>

<style scoped>
.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 70px;
  background: #ffffff;
  border-bottom: 1px solid #d9d9d9;
  z-index: 1001;
  padding: 0 24px 0 24px;
}

.header-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  max-width: 100%;
}

/* Left Section */
.header-left {
  display: flex;
  align-items: center;
}

.logo {
  display: flex;
  align-items: center;
}

.logo-full {
  height: 36px;
}

.logo-icon {
  display: none;
  height: 32px;
}

/* Center Section - Search */
.header-center {
  flex: 1;
  max-width: 480px;
  margin: 0 32px;
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;
}

.search-box input {
  width: 100%;
  height: 44px;
  padding: 0 48px 0 44px;
  border: 1px solid #d9d9d9;
  border-radius: 14px;
  background: #f5f5f5;
  font-size: 14px;
  color: #353535;
  transition: all 0.2s ease;
}

.search-box input:focus {
  outline: none;
  border-color: #003049;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(0, 48, 73, 0.1);
}

.search-box input::placeholder {
  color: #6b7280;
}

.search-icon {
  position: absolute;
  left: 14px;
  color: #6b7280;
  pointer-events: none;
}

.search-shortcut {
  position: absolute;
  right: 12px;
  padding: 4px 8px;
  background: #d9d9d9;
  border-radius: 6px;
  font-size: 11px;
  color: #353535;
  font-family: system-ui;
}

/* Right Section */
.header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-right: 16px;
  padding-right: 16px;
  border-right: 1px solid #d9d9d9;
}

.action-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  border-radius: 12px;
  color: #353535;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-btn:hover {
  background: #8ecae6;
  color: #003049;
}

.notification-badge {
  position: absolute;
  top: 6px;
  right: 6px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: #ff595e;
  border-radius: 9px;
  font-size: 11px;
  font-weight: 600;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* User Dropdown */
.user-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 12px 6px 6px;
  border: 1px solid #d9d9d9;
  background: #f5f5f5;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.user-btn:hover {
  background: #8ecae6;
  border-color: #8ecae6;
}

.user-avatar {
  position: relative;
  width: 36px;
  height: 36px;
}

.user-avatar.large {
  width: 48px;
  height: 48px;
}

.user-avatar img {
  width: 100%;
  height: 100%;
  border-radius: 12px;
  object-fit: cover;
}

.status-dot {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 10px;
  height: 10px;
  background: #22c55e;
  border: 2px solid white;
  border-radius: 50%;
}

.user-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
}

.user-name {
  font-size: 14px;
  font-weight: 600;
  color: #353535;
  line-height: 1.2;
}

.user-role {
  font-size: 12px;
  color: #6b7280;
}

.chevron {
  color: #6b7280;
  transition: transform 0.2s ease;
}

.user-dropdown.show .chevron {
  transform: rotate(180deg);
}

/* Dropdowns */
.dropdown-menu {
  min-width: 280px;
  padding: 0;
  border: 1px solid #d9d9d9;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-top: 8px;
}
</style>

<style scoped>
/* Notifications Dropdown */
.notifications-dropdown {
  width: 360px;
}

.dropdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #d9d9d9;
}

.dropdown-header h6 {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #353535;
}

.mark-read-btn {
  border: none;
  background: none;
  font-size: 13px;
  color: #003049;
  cursor: pointer;
}

.mark-read-btn:hover {
  text-decoration: underline;
}

.notifications-list {
  max-height: 320px;
  overflow-y: auto;
}

.notification-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 20px;
  text-decoration: none;
  transition: background 0.15s ease;
}

.notification-item:hover {
  background: #f5f5f5;
}

.notification-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  flex-shrink: 0;
}

.notification-icon.success {
  background: #dcfce7;
  color: #16a34a;
}

.notification-icon.info {
  background: #e0f2fe;
  color: #003049;
}

.notification-icon.warning {
  background: #fef3c7;
  color: #92400e;
}

.notification-icon.error {
  background: #fee2e2;
  color: #ff595e;
}

.notification-content p {
  margin: 0 0 4px;
  font-size: 14px;
  color: #353535;
  line-height: 1.4;
}

.notification-time {
  font-size: 12px;
  color: #6b7280;
}

.empty-notifications {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #6b7280;
}

.empty-notifications p {
  margin: 12px 0 0;
  font-size: 14px;
}

.dropdown-footer {
  display: block;
  padding: 14px 20px;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  color: #003049;
  text-decoration: none;
  border-top: 1px solid #d9d9d9;
  transition: background 0.15s ease;
}

.dropdown-footer:hover {
  background: #f5f5f5;
}

/* User Menu Dropdown */
.user-menu-dropdown {
  width: 280px;
}

.user-menu-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px;
  background: linear-gradient(135deg, #003049 0%, #073b4c 100%);
}

.user-menu-header .user-avatar img {
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.user-details h6 {
  margin: 0 0 2px;
  font-size: 15px;
  font-weight: 600;
  color: white;
}

.user-details p {
  margin: 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
}

.user-menu-body {
  padding: 8px;
}

.user-menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 10px;
  font-size: 14px;
  color: #353535;
  text-decoration: none;
  transition: all 0.15s ease;
}

.user-menu-item:hover {
  background: #8ecae6;
  color: #003049;
}

.user-menu-footer {
  padding: 8px;
  border-top: 1px solid #d9d9d9;
}

.logout-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px;
  border: none;
  background: #fee2e2;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  color: #ff595e;
  cursor: pointer;
  transition: all 0.15s ease;
}

.logout-btn:hover {
  background: #fee2e2;
}

/* Mobile */
.mobile-menu-btn {
  display: none;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: none;
  background: #f5f5f5;
  border-radius: 12px;
  color: #353535;
  cursor: pointer;
}

.mobile-menu-btn:hover {
  background: #8ecae6;
  color: #003049;
}

@media (max-width: 1024px) {
  .header-center {
    display: none;
  }
}

@media (max-width: 768px) {
  .header {
    padding: 0 16px;
  }

  .logo-full {
    display: none;
  }

  .logo-icon {
    display: block;
  }

  .header-actions {
    margin-right: 8px;
    padding-right: 8px;
  }

  .user-info {
    display: none;
  }

  .user-btn {
    padding: 4px;
    border-radius: 12px;
  }

  .chevron {
    display: none;
  }

  .mobile-menu-btn {
    display: flex;
  }
}
</style>
